import { useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../../lib/utils";

// A date field with a calendar in the melt theme, in place of the browser's
// <input type="date">. Values are "YYYY-MM-DD" strings ("" = no date), like the native input.

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** "2026-10-06" -> local Date (new Date("2026-10-06") would be UTC midnight, a day early in the Americas) */
function parse(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

function format(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const sameDay = (a: Date | null, b: Date) =>
  !!a && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

type DatePickerProps = {
  value: string;
  onChange: (value: string) => void;
  /** Styles the field itself, so it matches the inputs around it */
  className?: string;
  placeholder?: string;
  id?: string;
  "aria-label"?: string;
  /** Hides "Clear" for a date the form needs */
  required?: boolean;
};

export function DatePicker({ value, onChange, className, placeholder = "Pick a date", id, required, ...rest }: DatePickerProps) {
  const selected = parse(value);
  const [open, setOpen] = useState(false);
  // First day of the month on screen
  const [month, setMonth] = useState(() => {
    const d = selected ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const onOpenChange = (next: boolean) => {
    if (next) {
      const d = parse(value) ?? new Date();
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setOpen(next);
  };

  const pick = (date: Date | null) => {
    onChange(date ? format(date) : "");
    setOpen(false);
  };

  const today = new Date();
  // 6 rows, starting on the Sunday on or before the 1st
  const start = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay());
  const days = Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  const shift = (n: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          aria-label={rest["aria-label"]}
          className={cn("flex items-center justify-between gap-2 text-left", className)}
        >
          <span className={cn("truncate", !selected && "text-melt-text3")}>
            {selected
              ? selected.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : placeholder}
          </span>
          <Calendar size={14} strokeWidth={1.75} className="shrink-0 text-melt-text3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        data-date-picker
        className="w-[252px] rounded-[11px] border-melt-hair bg-melt-menu p-3 font-grotesk text-melt-text1 shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => shift(-1)}
            aria-label="Previous month"
            className="grid h-7 w-7 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
          >
            <ChevronLeft size={16} strokeWidth={1.75} />
          </button>
          <span className="text-[13.5px] font-semibold">
            {month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </span>
          <button
            type="button"
            onClick={() => shift(1)}
            aria-label="Next month"
            className="grid h-7 w-7 place-items-center rounded-[7px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
          >
            <ChevronRight size={16} strokeWidth={1.75} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 text-center">
          {WEEKDAYS.map((d) => (
            <span key={d} className="pb-1 font-plex text-[10.5px] uppercase text-melt-text3">
              {d}
            </span>
          ))}
          {days.map((day) => {
            const isSelected = sameDay(selected, day);
            const isToday = sameDay(today, day);
            const inMonth = day.getMonth() === month.getMonth();
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => pick(day)}
                aria-label={day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                aria-pressed={isSelected}
                className={cn(
                  "h-8 rounded-[7px] text-[13px] transition-colors",
                  isSelected
                    ? "bg-melt-caramel font-medium text-white hover:bg-melt-press"
                    : cn(
                        "hover:bg-melt-text1/[.07]",
                        inMonth ? "text-melt-text1" : "text-melt-text3/50",
                        isToday && "font-semibold text-melt-caramel"
                      )
                )}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>

        <div className="mt-2 flex items-center justify-between border-t border-melt-line pt-2 text-[12.5px]">
          {!required && value ? (
            <button type="button" onClick={() => pick(null)} className="rounded-md px-1.5 py-1 text-melt-text3 hover:text-melt-text1">
              Clear
            </button>
          ) : (
            <span />
          )}
          <button type="button" onClick={() => pick(today)} className="rounded-md px-1.5 py-1 font-medium text-melt-caramel hover:text-melt-press">
            Today
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
